import { formatDateToLocale } from '@utils';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { CatalogUerOtherCostTemplateData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { Checkbox, Loader } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useMemo } from 'react';

import { catalogOtherCostTemplatesNameFilter } from '../catalogUerOtherCostTemplatesQueries';
import { RowMenu } from '../RowMenu';

interface ICatalogOtherCostTemplatesColumns {
    handleSetCheckedIds: Dispatch<SetStateAction<{ [key: number]: boolean }>>;
    checkedIds: { [key: number]: boolean };
    createOrEditModalHandler: (state: boolean) => void;
    setOpenedTemplateId: Dispatch<SetStateAction<number | null>>;
    onRemoveSuccess: () => void;
    setFilterValues: Dispatch<SetStateAction<Record<string, string[]>>>;
    catalogOtherCostTemplatesRules: IUseAccess['catalogOtherCostTemplates'];
}

export const useCatalogOtherCostTemplatesColumns = ({
    checkedIds,
    handleSetCheckedIds,
    createOrEditModalHandler,
    setOpenedTemplateId,
    onRemoveSuccess,
    setFilterValues,
    catalogOtherCostTemplatesRules,
}: ICatalogOtherCostTemplatesColumns) => {
    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                handleSetCheckedIds((ids: any) => ({ ...ids, [rowId]: checked }));
            },
        [handleSetCheckedIds],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue: any) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [setFilterValues],
    );

    const columns = useMemo<ColDef<CatalogUerOtherCostTemplateData>[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                maxWidth: 80,
                minWidth: 80,
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data: CatalogUerOtherCostTemplateData }) =>
                    !data ? (
                        <Loader />
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                disabled={!catalogOtherCostTemplatesRules.canDelete}
                                checked={checkedIds[data.id]}
                                onChange={handleChangeCheckbox(data.id)}
                            />
                            <RowMenu
                                createOrEditModalHandler={createOrEditModalHandler}
                                data={data}
                                setOpenedTemplateId={setOpenedTemplateId}
                                onRemoveSuccess={onRemoveSuccess}
                                catalogOtherCostTemplatesRules={catalogOtherCostTemplatesRules}
                            />
                        </div>
                    ),
            },
            {
                headerName: 'Наименование',
                field: 'name',
                minWidth: 150,
                flex: 1,
                sortable: true,
                initialSort: 'asc',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogUEROtherCostTemplates',
                    whereVariables: ['name'],
                    childFieldAccessor: 'name',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'name',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogOtherCostTemplatesNameFilter,
                },
            },
            {
                headerName: 'Дата обновления',
                field: 'updatedAt',
                spanHeaderHeight: true,
                valueFormatter: ({ value }) => formatDateToLocale(value),
                width: 200,
            },
        ],
        [
            catalogOtherCostTemplatesRules,
            changeFilterHandler,
            checkedIds,
            createOrEditModalHandler,
            handleChangeCheckbox,
            onRemoveSuccess,
            setOpenedTemplateId,
        ],
    );

    return { columns };
};
